
import * as React from 'react';
import { scrollIntoView } from 'app/infrastructure/platform/browser-dom';
import { ButtonTertiary } from 'ui';
import { debounceRender } from 'ui/common/debounce-render';
import { ExpanderBackground } from 'ui/common/dialog-expander/expander-background/expander-background';
import { ContextMenu } from 'ui/tile/context-menu';
import { ButtonSecondary, ValidationLevel } from 'ui/common';
import { OpenStateInjectedProps, withOpenState } from 'ui/common/enhancers';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { MdEdit } from 'react-icons/md';
import { ModalSize } from 'app/components/constants';
import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';
import { TileProps, TileState } from 'ui/tile/tile';
import tileStyles from 'ui/tile/tile.scss';
import styles from './compact-header-tile.scss';

type CompactHeaderTileProps = TileProps & OpenStateInjectedProps;

interface DetailViewProps {
  dialogTitle: string;
  textDone: string;
  textDetailsEditButton: string;
  showDetailsEditButton: boolean;
  expandAll: boolean;
}

interface ModalDetailsProps {
  dialogTitle: string;
  textDone: string;
  textDetailsEditButton: string;
  showDetailsEditButton: boolean;
}

interface ExpandedLayoutProps {
  componentId: string;
  tileWrapperClasses: string;
  detailViewExpanded: React.ReactNode;
  detailView: React.ReactNode;
  isActivated: boolean;
}

interface StandardLayoutProps {
  componentId: string;
  tileWrapperClasses: string;
  contextMenuClassNames: string;
  withShowDetailsButtonStyle: string;
  viewDetailsButton: React.ReactNode;
  editDetailsButton: React.ReactNode;
  detailView: React.ReactNode;
  shouldRenderOpenDetailView: boolean;
  print: boolean;
  expandAll: boolean;
}

interface TileRenderModel {
  componentId: string;
  tileWrapperClasses: string;
  detailViewExpanded: React.ReactNode;
  detailView: React.ReactNode;
  isActivated: boolean;
  contextMenuClassNames: string;
  withShowDetailsButtonStyle: string;
  viewDetailsButton: React.ReactNode;
  editDetailsButton: React.ReactNode;
  shouldRenderOpenDetailView: boolean;
  print: boolean;
  expandAll: boolean;
}

class CompactHeaderTileComponent extends React.Component<
  CompactHeaderTileProps,
  TileState
> {
  private ref: HTMLElement | null = null;

  public static defaultProps: Partial<TileProps> = {
    enabledChildEntityOperations: { remove: false },
    textKeyLabel: ''
  };

  public state: Readonly<TileState> = {
    hideImmediately: false
  };

  private handleSelect = () => {
    this.setState({ hideImmediately: false }, () =>
      this.props.onSelectDocumentItem && this.props.onSelectDocumentItem()
    );

    !this.props.expandAll && this.ref && scrollIntoView(this.ref);
    this.props.open();
  };

  private handleEdit = () => {
    this.props.onEditDocumentItem && this.props.onEditDocumentItem();
  };

  private handleDelete = () => {
    this.props.onDeleteDocumentItem && this.props.onDeleteDocumentItem();
  };

  private handleClose = () => {
    this.props.onSelectDocumentItem?.();

    this.setState({ hideImmediately: true }, () =>
      this.props.onSelectDocumentItem?.(null)
    );

    this.props.close();
  };

  private renderEditDetailsButton = () => {
    const {
      shouldEditDocumentItem,
      textDetailsEditButton,
      shouldHideEditDocumentIcon
    } = this.props;

    if (shouldHideEditDocumentIcon) {
      return null;
    }

    return (
      <div
        className={tileStyles.buttonContainer}
        title={textDetailsEditButton}
        onClick={shouldEditDocumentItem ? this.handleEdit : this.handleSelect}
      >
        <MdEdit className={tileStyles.buttonIcons} />
      </div>
    );
  };

  private renderViewDetailsButton = () => {
    const { textShowDetails, validationDescendants } = this.props;

    const childrenHasErrors = !!validationDescendants?.some(
      v => v.level === ValidationLevel.error
    );

    const childrenHasWarnings = !!validationDescendants?.some(
      v => v.level === ValidationLevel.warning
    );

    const validationClass = childrenHasErrors
      ? tileStyles.hasChildErrors
      : childrenHasWarnings
        ? tileStyles.hasChildWarnings
        : '';

    return (
      <div
        className={tileStyles.buttonContainer}
        onClick={this.handleSelect}
      >
        <ButtonTertiary
          classNames={`${tileStyles.detailsButton} ${validationClass}`}
          onClick={this.handleSelect}
        >
          {textShowDetails}
        </ButtonTertiary>
      </div>
    );
  };

  private addRef = (el: HTMLDivElement | null) => {
    this.ref = el;
  };

  private renderExpandedDetails = ({
    dialogTitle,
    textDone,
    textDetailsEditButton,
    showDetailsEditButton,
    expandAll
  }: DetailViewProps) => {
    const detailsEditButton = showDetailsEditButton ? (
      <ButtonSecondary
        classNames={tileStyles.editButton}
        onClick={this.handleEdit}
      >
        {textDetailsEditButton}
      </ButtonSecondary>
    ) : null;

    return (
      <DialogExpander
        expandAll={true}
        onClose={this.handleClose}
        title={dialogTitle}
        hideTitle={false}
        textDone={textDone}
        extraFooterActions={[detailsEditButton]}
        className="tile-details-view"
      >
        {expandAll && (
          <ContextMenu {...this.props} onDelete={this.handleDelete} />
        )}

        {this.props.details}

        {expandAll && (
          <div className={tileStyles.editButtonExpandAll}>
            {detailsEditButton}
          </div>
        )}
      </DialogExpander>
    );
  };

  private renderModalDetails = ({
    dialogTitle,
    textDone,
    textDetailsEditButton,
    showDetailsEditButton
  }: ModalDetailsProps) => {
    const detailsEditButton = showDetailsEditButton ? (
      <ButtonSecondary
        classNames={tileStyles.editButton}
        onClick={this.handleEdit}
      >
        {textDetailsEditButton}
      </ButtonSecondary>
    ) : null;

    return (
      <ModalWrapper
        onClose={this.handleClose}
        onDone={this.handleClose}
        headerTitle={dialogTitle}
        btnTitle={textDone}
        btnExtraFooterActionOverride={detailsEditButton}
        size={ModalSize.xtraLarge}
        textClose={textDone}
      >
        {this.props.details}
      </ModalWrapper>
    );
  };

  private renderExpandedLayout = ({
    componentId,
    tileWrapperClasses,
    detailViewExpanded,
    detailView,
    isActivated
  }: ExpandedLayoutProps) => (
    <div
      id={componentId}
      ref={this.addRef}
      className={tileWrapperClasses}
    >
      {detailViewExpanded}

      {isActivated && (
        <span className={tileStyles.activatedDetailView}>
          {detailView}
        </span>
      )}
    </div>
  );

  private renderStandardLayout = ({
    componentId,
    tileWrapperClasses,
    contextMenuClassNames,
    withShowDetailsButtonStyle,
    viewDetailsButton,
    editDetailsButton,
    detailView,
    shouldRenderOpenDetailView,
    print,
    expandAll
  }: StandardLayoutProps) => (
    <>
      {shouldRenderOpenDetailView && (
        <div tabIndex={0} onFocus={this.handleClose} />
      )}

      <div id={componentId} className={tileWrapperClasses}>
        <div
          className={`${tileStyles.tile} ${contextMenuClassNames} ${withShowDetailsButtonStyle}`}
        >
          <div className={tileStyles.btnIconsWrapper}>
            {viewDetailsButton}
            {editDetailsButton}
            <ContextMenu {...this.props} onDelete={this.handleDelete} />
          </div>

          <div className={`tilevalues ${styles.tileValuesWrapper}`}>
            {this.props.children}
          </div>
        </div>

        {(this.props.isOpen || shouldRenderOpenDetailView) && detailView}

        {shouldRenderOpenDetailView && !(print || expandAll) && (
          <ExpanderBackground onClick={this.handleClose} />
        )}
      </div>
    </>
  );

  private getTileWrapperClasses = (
    shouldRenderOpenDetailView: boolean,
    expandAll: boolean
  ): string => {
    const activeClass =
      shouldRenderOpenDetailView && !expandAll
        ? tileStyles.activeTile
        : '';

    return `${this.props.className} ${tileStyles.tileWrapper} ${activeClass}`;
  };

  private getContextMenuClassNames = (): string =>
    this.props.enabledChildEntityOperations?.remove
      ? tileStyles.hasMeatball
      : '';

  private getShowDetailsButtonStyle = (
    showDetailsButton: boolean,
    hasDetails: boolean,
    expandAll: boolean
  ): string =>
    showDetailsButton && hasDetails && !expandAll
      ? tileStyles.withShowDetailsButton
      : '';

    private shouldRenderOpenDetailView = (): boolean => {
      const { details, print, expandAll, showDetailsButton = true, activate } = this.props;

      const isActivated =
        showDetailsButton && !this.state.hideImmediately && activate;

      return !!details && !!(print || expandAll || isActivated);
  };

    private createDetailViews = (
    dialogTitle: string
  ): Pick<TileRenderModel, 'detailViewExpanded' | 'detailView'> => {
    const {
      textDone,
      textDetailsEditButton,
      showDetailsEditButton = false,
      expandAll
    } = this.props;

    if (!this.shouldRenderOpenDetailView()) {
      return {
        detailViewExpanded: null,
        detailView: null
      };
    }

    const detailProps: DetailViewProps = {
      dialogTitle,
      textDone,
      textDetailsEditButton,
      showDetailsEditButton,
      expandAll: !!expandAll
    };

    return {
      detailViewExpanded: this.renderExpandedDetails(detailProps),
      detailView: this.renderModalDetails(detailProps)
    };
  };

  private createDetailButtons = () => {
    const {
      details,
      showDetailsButton = true,
      showDetailsEditButton = false
    } = this.props;

    return {
      viewDetailsButton:
        showDetailsButton && details
          ? this.renderViewDetailsButton()
          : null,
      editDetailsButton:
        showDetailsEditButton && details
          ? this.renderEditDetailsButton()
          : null
    };
  };

  private createRenderModel = (): TileRenderModel => {
  const {
    componentId,
    primaryKey,
    textKeyLabel,
    showDetailsButton = true,
    expandAll,
    activate,
    print,
    details
  } = this.props;

  const dialogTitle = `${textKeyLabel} #${primaryKey?.value || ''}`;
  const shouldRender = this.shouldRenderOpenDetailView();

  const isActivated = !!(
    showDetailsButton &&
    !this.state.hideImmediately &&
    activate
  );

  const detailViews = this.createDetailViews(dialogTitle);
  const buttons = this.createDetailButtons();

  return {
    componentId,
    tileWrapperClasses: this.getTileWrapperClasses(shouldRender, !!expandAll),
    ...detailViews,
    ...buttons,
    isActivated,
    contextMenuClassNames: this.getContextMenuClassNames(),
    withShowDetailsButtonStyle: this.getShowDetailsButtonStyle(
      !!showDetailsButton,
      !!details,
      !!expandAll
    ),
    shouldRenderOpenDetailView: shouldRender,
    print: !!print,
    expandAll: !!expandAll
  };
};

  private renderExpandedTile = (model: TileRenderModel) =>
    this.renderExpandedLayout({
      componentId: model.componentId,
      tileWrapperClasses: model.tileWrapperClasses,
      detailViewExpanded: model.detailViewExpanded,
      detailView: model.detailView,
      isActivated: model.isActivated
    });

  private renderStandardTile = (model: TileRenderModel) =>
    this.renderStandardLayout({
      componentId: model.componentId,
      tileWrapperClasses: model.tileWrapperClasses,
      contextMenuClassNames: model.contextMenuClassNames,
      withShowDetailsButtonStyle: model.withShowDetailsButtonStyle,
      viewDetailsButton: model.viewDetailsButton,
      editDetailsButton: model.editDetailsButton,
      detailView: model.detailView,
      shouldRenderOpenDetailView: model.shouldRenderOpenDetailView,
      print: model.print,
      expandAll: model.expandAll
    });

  public render() {
    const model = this.createRenderModel();

    if (model.expandAll && this.props.details) {
      return this.renderExpandedTile(model);
    }

    return this.renderStandardTile(model);
  }
}

const t = withOpenState()(debounceRender(CompactHeaderTileComponent));

export { t as CompactHeaderTile };
